import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const BMICalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [height, setHeight] = useState('170');
  const [weight, setWeight] = useState('70');
  const [age, setAge] = useState('30');
  const [gender, setGender] = useState('male');
  const [activity, setActivity] = useState('moderate');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const h = parseFloat(parseBrazilianNumber(height)) / 100; // Converter cm para m
    const w = parseFloat(parseBrazilianNumber(weight));
    const a = parseInt(parseBrazilianNumber(age));

    if (!h || !w || h <= 0 || w <= 0) return;

    const imc = w / (h * h);

    // Classificação OMS
    let category = '';
    let color = '';
    let recommendation = '';

    if (imc < 18.5) {
      category = 'Abaixo do Peso';
      color = 'blue';
      recommendation = 'Consulte um nutricionista para ganho de peso saudável';
    } else if (imc < 25) {
      category = 'Peso Normal';
      color = 'green';
      recommendation = 'Continue com hábitos saudáveis e atividade física regular';
    } else if (imc < 30) {
      category = 'Sobrepeso';
      color = 'yellow';
      recommendation = 'Considere reajustar dieta e aumentar atividade física';
    } else if (imc < 35) {
      category = 'Obesidade Grau I';
      color = 'red';
      recommendation = 'Busque orientação médica para plano de emagrecimento';
    } else if (imc < 40) {
      category = 'Obesidade Grau II';
      color = 'red';
      recommendation = 'Acompanhamento médico e nutricional é essencial';
    } else {
      category = 'Obesidade Grau III';
      color = 'red';
      recommendation = 'Busque ajuda médica especializada imediatamente';
    }

    // Cálculo de peso ideal (fórmula de Devine corrigida)
    const idealWeightMale = 50 + 2.3 * ((h * 100) - 152.4) / 2.54;
    const idealWeightFemale = 45.5 + 2.3 * ((h * 100) - 152.4) / 2.54;
    const idealWeight = gender === 'male' ? idealWeightMale : idealWeightFemale;

    // TMB usando fórmula de Harris-Benedict
    let tmb;
    if (gender === 'male') {
      tmb = 88.362 + (13.397 * w) + (4.799 * (h * 100)) - (5.677 * a);
    } else {
      tmb = 447.593 + (9.247 * w) + (3.098 * (h * 100)) - (4.330 * a);
    }

    // Fator de atividade
    const activityFactors = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      very_active: 1.9
    };

    const dailyCalories = tmb * (activityFactors[activity as keyof typeof activityFactors] || 1.55);

    const res = {
      imc: imc.toFixed(1),
      category,
      color,
      recommendation,
      idealWeight: idealWeight.toFixed(1),
      weightDifference: (w - idealWeight).toFixed(1),
      tmb: Math.round(tmb),
      dailyCalories: Math.round(dailyCalories)
    };

    setResult(res);

    onResultChange([
      { label: 'Altura', value: `${height} cm` },
      { label: 'Peso', value: `${weight} kg` },
      { label: 'IMC', value: res.imc },
      { label: 'Categoria', value: res.category },
      { label: 'Peso Ideal', value: `${res.idealWeight} kg` },
      { label: 'TMB', value: `${res.tmb} kcal` },
      { label: 'Calorias Diárias', value: `${res.dailyCalories} kcal` }
    ]);
  };

  return (
    <div className="space-y-8">
      {/* Explicação Inicial */}
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">Calculadora de IMC Completa</h2>
        <p className="text-theme-muted mb-4">
          Calcule seu Índice de Massa Corporal e receba análises detalhadas sobre seu peso ideal,
          metabolismo basal e necessidades calóricas diárias.
        </p>
        <div className="flex gap-2">
          <span className="px-3 py-1 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-bold">Saúde</span>
          <span className="px-3 py-1 bg-green-50 dark:bg-emerald-950/30 text-green-600 dark:text-emerald-400 rounded-full text-xs font-bold">Fitness</span>
          <span className="px-3 py-1 bg-purple-50 dark:bg-purple-950/30 text-purple-600 dark:text-purple-400 rounded-full text-xs font-bold">Nutrição</span>
        </div>
      </div>

      {/* Formulário */}
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Seus Dados</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Altura (cm)" placeholder="Ex: 170" value={height} onChange={setHeight} required isInteger type="text" />
          <FormField label="Peso (kg)" placeholder="Ex: 70" value={weight} onChange={setWeight} required isNumeric type="text" />
          <FormField label="Idade" placeholder="Ex: 30" value={age} onChange={setAge} required isInteger type="text" />
          <FormField label="Sexo" value={gender} onChange={setGender} type="select" required options={[{ value: 'male', label: 'Masculino' }, { value: 'female', label: 'Feminino' }]} />
          <div className="md:col-span-2">
            <FormField
              label="Nível de Atividade"
              value={activity}
              onChange={setActivity}
              type="select"
              required
              options={[
                { value: 'sedentary', label: 'Sedentário (pouco ou nenhum exercício)' },
                { value: 'light', label: 'Leve (exercício leve 1-3 dias/semana)' },
                { value: 'moderate', label: 'Moderado (exercício moderado 3-5 dias/semana)' },
                { value: 'active', label: 'Ativo (exercício intenso 6-7 dias/semana)' },
                { value: 'very_active', label: 'Muito Ativo (exercício muito intenso)' }
              ]}
            />
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={calculate}
            className="px-12 py-5 bg-accent-gradient text-white rounded-2xl font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center gap-3 uppercase text-sm tracking-widest"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular IMC
          </button>
        </div>
      </div>

      {/* Resultados */}
      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ResultCard title="Seu IMC" value={result.imc} description={result.category} color={result.color as any} />
            <ResultCard title="Peso Ideal" value={`${result.idealWeight} kg`} description={`Diferença: ${result.weightDifference} kg`} color="emerald" />
            <ResultCard title="Metabolismo Basal" value={`${result.tmb} kcal`} description="Calorias em repouso" color="amber" />
            <ResultCard title="Calorias Diárias" value={`${result.dailyCalories} kcal`} description="Para manter peso" color="indigo" />
          </div>

          <EducationalBlock
            title="Informações Importantes"
            tips={[
              `Recomendação: ${result.recommendation}`,
              "IMC não distingue massa muscular de gordura",
              "Atletas podem ter IMC elevado sem estarem acima do peso",
              "Consulte sempre um profissional de saúde"
            ]}
          />
        </>
      )}
    </div>
  );
};

export default BMICalculator;
